import {
  addExternalTransferPharmacyApi,
  deleteExternalTransferPharmacyApi,
  editExternalTransferPharmacyApi,
  getFulfillingPharmaciesApi,
  listExternalPharmaciesApi,
  transferOrderToFacilityApi,
  updateFulfillingPharmacyShippingDefaultApi,
} from 'api/pharmacy';
import { pharmacyActions } from 'store/slices/pharmacySlice';
import { systemConfigActions } from 'store/slices/systemConfigSlice';
import { handleError } from './errorHandlerThunks';
import { listPatientOrders } from './patientThunks';

export const getFulfillingPharmacies = () => (dispatch, getState) => {
  const { currentTenantId } = getState().auth;

  return getFulfillingPharmaciesApi({ pharmacyId: currentTenantId })
    .then((fulfillingPharmacies) =>
      dispatch(
        pharmacyActions.loadPharmacyData({ fulfillingPharmacies, tenantId: currentTenantId })
      )
    )
    .catch((error) => dispatch(handleError({ error })));
};

export const transferOrderToFacility =
  ({ orderIds, lineItemIds, fulfillingPharmacyId, toFacilityId, mpi }) =>
  (dispatch) =>
    transferOrderToFacilityApi({
      orderIds,
      lineItemIds,
      fulfillingPharmacyId: fulfillingPharmacyId || toFacilityId,
    })
      // POM-5945: resolves with the response envelope { results, outcomes, totals }, passed through
      // whole — the caller needs outcomes[].reason and outcomes[].lineItemIds, not just a boolean.
      // A rejection still resolves to undefined via handleError, which the caller reads as ERROR.
      .then((transferResponse) => {
        // Refresh only when something actually moved; a fully blocked transfer changed nothing.
        if (mpi && transferResponse?.totalTransferred > 0) {
          dispatch(listPatientOrders({ mpi }));
        }

        return transferResponse;
      })
      .catch((error) => dispatch(handleError({ error })));

export const listExternalPharmacies =
  ({ sortBy, showLoading } = {}) =>
  (dispatch, getState) => {
    const defaultSortBy = { sortKey: 'pharmacyName', sortOrder: 'ASC' };
    const currentSortBy =
      sortBy || getState().systemConfigs.externalPharmacy.sortBy || defaultSortBy;
    const currentPharmacyList = getState().systemConfigs.externalPharmacy.pharmacyList;

    const shouldShowLoading = showLoading || currentPharmacyList === null;

    if (shouldShowLoading) {
      dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: true }));
    }

    return listExternalPharmaciesApi({ sortBy: currentSortBy })
      .then((response) => {
        dispatch(
          systemConfigActions.updateExternalPharmacies({
            pharmacyList: response?.results || [],
          })
        );
        if (shouldShowLoading) {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
        }
        return response;
      })
      .catch((error) => {
        if (shouldShowLoading) {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
        }
        dispatch(handleError({ error }));
      });
  };

export const addExternalTransferPharmacy = (input) => (dispatch) => {
  dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: true }));

  return addExternalTransferPharmacyApi(input)
    .then((pharmacy) => {
      return dispatch(listExternalPharmacies({ showLoading: true }))
        .then(() => {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
          return pharmacy;
        })
        .catch((error) => {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
          throw error;
        });
    })
    .catch((error) => {
      dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
      dispatch(handleError({ error }));
      return null;
    });
};

export const editExternalTransferPharmacy = (input) => (dispatch) => {
  dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: true }));

  return editExternalTransferPharmacyApi(input)
    .then((pharmacy) => {
      return dispatch(listExternalPharmacies({ showLoading: true }))
        .then(() => {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
          return pharmacy;
        })
        .catch((error) => {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
          throw error;
        });
    })
    .catch((error) => {
      dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
      dispatch(handleError({ error }));
      return null;
    });
};

export const updateFulfillingPharmacyShippingDefault =
  ({ fulfillingPharmacyId, routeTo }) =>
  (dispatch) => {
    return updateFulfillingPharmacyShippingDefaultApi({ fulfillingPharmacyId, routeTo })
      .then((result) => {
        if (result) {
          dispatch(getFulfillingPharmacies());
        }
        return result;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return null;
      });
  };

export const deleteExternalTransferPharmacy = (pharmacyId) => (dispatch) => {
  dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: true }));

  return deleteExternalTransferPharmacyApi(pharmacyId)
    .then(() => {
      return dispatch(listExternalPharmacies({ showLoading: true }))
        .then(() => {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
          return true;
        })
        .catch((error) => {
          dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
          throw error;
        });
    })
    .catch((error) => {
      dispatch(systemConfigActions.setExternalPharmaciesLoading({ loading: false }));
      dispatch(handleError({ error }));
      return null;
    });
};
